// Pure logic of an Enroll Hosts run (PCD-9663). No React, no API calls, no clock reads.
// See enrollmentRuns.spec.md.
import { toErrorOutcome } from 'app/plugins/infrastructure/components/common/service-health/storage-connectivity/helpers'
import type { VerifyBackendResult } from 'app/plugins/infrastructure/components/common/service-health/storage-connectivity/model'
import { roleStatusMap } from 'app/plugins/openstack/components/infrastructure/selectors'
import type { EnrollmentRun, EnrollmentSelection, HostEnrollment, StorageCheck } from './model'

export const RETRY_DELAY_MS = 15000
export const RETRY_WINDOW_MS = 300000
export const MAX_SAVED_RUNS = 10
export const GAVE_UP_TEXT = "cinder-volume hasn't started on this host."
export const HOST_GONE_TEXT = 'Host is no longer available.'
export const ROLES_REMOVED_TEXT = 'Roles were removed from this host.'
export const TIMEOUT_TEXT = "The storage service didn't answer within 45 seconds."
// No answer by then counts as the same "not ready yet" as a 504, so the queue never stalls.
export const CHECK_TIMEOUT_MS = 45000

// Advanced Remote Support is a v1 role: its status is in the host's v1RoleDetails.
const SUPPORT_ROLE = 'pf9-support'
const FAILED_STATUSES = ['not-applied', 'auth-error', 'partially-applied']
const STORAGE_ROLE = 'persistent-storage'
const REMOVING_STATUSES = [
  'start-deauth',
  'pre-deauth',
  'deauth-converging',
  'deauth-converged',
  'deauth-error',
]

// The bits of a resmgr host (as listed by listResmgrHosts) this module reads.
export interface ResmgrHostStatus {
  id: string
  roles_status_details?: Record<string, string>
  v1RoleDetails?: Record<string, string> // v1 roles, e.g. pf9-support
}

export type CheckAnswer = { result: VerifyBackendResult } | { error: unknown } | { timedOut: true }

// ENR-FR-002: resmgr role keys a run waits for (order does not matter).
export const rolesForSelection = (selection: EnrollmentSelection): string[] =>
  [
    selection.hypervisor && 'hypervisor',
    selection.backends.length > 0 && 'persistent-storage',
    selection.imageLibrary && 'image-library',
    selection.remoteSupport && SUPPORT_ROLE,
    selection.dns && 'dns',
  ].filter(Boolean) as string[]

// ENR-FR-001: a new run, every host waiting for its assign calls.
export const createRun = ({
  id,
  region,
  now,
  selection,
  hosts,
}: {
  id: string
  region: string
  now: Date
  selection: EnrollmentSelection
  hosts: { id: string; name: string }[]
}): EnrollmentRun => ({
  id,
  region,
  createdAt: now.toISOString(),
  selection,
  hosts: hosts.map((host) => ({
    hostId: host.id,
    hostName: host.name,
    phase: 'preparing',
    roles: {},
  })),
  checks: [],
})

// ENR-FR-002: a host's phase from resmgr. Settled hosts never change. Returns the same object
// when nothing changed, so callers can tell a real transition from a poll tick.
export const deriveHostPhase = (
  host: HostEnrollment,
  resmgrHost: ResmgrHostStatus | undefined,
  roleKeys: string[],
  now: Date,
): HostEnrollment => {
  if (host.phase === 'completed' || host.phase === 'failed') return host
  if (!resmgrHost) return { ...host, phase: 'failed', error: HOST_GONE_TEXT }

  const roles: Record<string, string> = {}
  for (const key of roleKeys) {
    const details =
      key === SUPPORT_ROLE ? resmgrHost.v1RoleDetails : resmgrHost.roles_status_details
    const status = details?.[key]
    if (status) roles[key] = status
  }
  const statuses = Object.values(roles)
  const failed = statuses.find((status) => FAILED_STATUSES.includes(status))
  // The host was deauthorized: its roles are being removed, or were and resmgr no longer reports
  // them. A host only gets to "applying" once resmgr reported a role, so none now means removed.
  const removed =
    statuses.some((status) => REMOVING_STATUSES.includes(status)) ||
    (host.phase === 'applying' && !statuses.length)

  let next: HostEnrollment
  if (removed) {
    next = { ...host, roles, phase: 'failed', error: ROLES_REMOVED_TEXT }
  } else if (failed) {
    next = { ...host, roles, phase: 'failed', error: roleStatusMap[failed] }
  } else if (roleKeys.every((key) => roles[key] === 'applied')) {
    next = { ...host, roles, phase: 'completed', appliedAt: now.toISOString() }
  } else {
    // Stays "preparing" until resmgr reports any role for this host.
    next = { ...host, roles, phase: statuses.length ? 'applying' : host.phase }
  }
  return JSON.stringify(next) === JSON.stringify(host) ? host : next
}

// ENR-FR-003: once every host settled, one check per (host, backend). Failed hosts get skipped
// checks. Runs without backends, or that already have checks, are returned as is.
export const queueChecks = (run: EnrollmentRun, now: Date): EnrollmentRun => {
  const settled = run.hosts.every((host) => host.phase === 'completed' || host.phase === 'failed')
  if (!settled || run.checks.length || !run.selection.backends.length) return run
  const checks: StorageCheck[] = run.hosts.flatMap((host) =>
    run.selection.backends.map((backend): StorageCheck => {
      const base = { serviceHost: `${host.hostId}@${backend}`, hostId: host.hostId, backend }
      return host.phase === 'completed'
        ? { ...base, state: 'queued', windowStart: host.appliedAt ?? now.toISOString() }
        : { ...base, state: 'skipped', skipReason: 'roleFailed' }
    }),
  )
  return { ...run, checks }
}

// ENR-FR-002, ENR-FR-003: refresh every host from resmgr, then queue checks if all settled.
export const updateRunHosts = (
  run: EnrollmentRun,
  resmgrHosts: ResmgrHostStatus[],
  now: Date,
): EnrollmentRun => {
  const roleKeys = rolesForSelection(run.selection)
  const hosts = run.hosts.map((host) =>
    deriveHostPhase(
      host,
      resmgrHosts.find((item) => item.id === host.hostId),
      roleKeys,
      now,
    ),
  )
  const changed = hosts.some((host, index) => host !== run.hosts[index])
  return skipRemovedChecks(queueChecks(changed ? { ...run, hosts } : run, now), resmgrHosts)
}

// ENR-FR-009: a queued check whose host lost its Persistent Storage role (removed, or being
// removed, after it was applied) is skipped instead of sent. Same object when nothing changed.
export const skipRemovedChecks = (
  run: EnrollmentRun,
  resmgrHosts: ResmgrHostStatus[],
): EnrollmentRun => {
  const removed = (hostId: string) => {
    const status = resmgrHosts.find((item) => item.id === hostId)?.roles_status_details?.[
      STORAGE_ROLE
    ]
    return !status || REMOVING_STATUSES.includes(status)
  }
  // An empty list means resmgr hosts aren't loaded yet, not that every host is gone.
  if (!resmgrHosts.length) return run
  if (!run.checks.some((check) => check.state === 'queued' && removed(check.hostId))) return run
  return {
    ...run,
    checks: run.checks.map((check) =>
      check.state === 'queued' && removed(check.hostId)
        ? { ...check, state: 'skipped', skipReason: 'roleRemoved', notBefore: undefined }
        : check,
    ),
  }
}

// ENR-FR-004: the next check to send. Nothing while any check is in flight, app wide.
export const nextCheck = (
  runs: EnrollmentRun[],
  now: Date,
): { runId: string; serviceHost: string } | undefined => {
  const checks = runs.flatMap((run) => run.checks)
  if (checks.some((check) => check.state === 'running')) return undefined
  const ordered = [...runs].sort((a, b) => a.createdAt.localeCompare(b.createdAt))
  for (const run of ordered) {
    const check = run.checks.find(
      (item) =>
        item.state === 'queued' &&
        (!item.notBefore || new Date(item.notBefore).getTime() <= now.getTime()),
    )
    if (check) return { runId: run.id, serviceHost: check.serviceHost }
  }
  return undefined
}

const updateCheck = (
  run: EnrollmentRun,
  serviceHost: string,
  update: (check: StorageCheck) => StorageCheck,
): EnrollmentRun => ({
  ...run,
  checks: run.checks.map((check) => (check.serviceHost === serviceHost ? update(check) : check)),
})

// ENR-FR-004: the check is in flight.
export const markRunning = (run: EnrollmentRun, serviceHost: string): EnrollmentRun =>
  updateCheck(run, serviceHost, (check) => ({ ...check, state: 'running' }))

// ENR-FR-005: apply one verify-backend answer. A 504, or a 404 carrying Cinder's own message
// ("Can't find service..."), means cinder-volume isn't up yet: retry in 15 s while the 5 minute
// window is open, then give up. A 404 with the route-not-found text means the API is missing
// and is final like every other error. 401 is never retried.
export const applyCheckAnswer = (
  run: EnrollmentRun,
  serviceHost: string,
  answer: CheckAnswer,
  now: Date,
): EnrollmentRun =>
  updateCheck(run, serviceHost, (check) => {
    const cleared = { ...check, notBefore: undefined }
    if ('result' in answer) {
      const state = answer.result.driver_initialized === false ? 'driverFailed' : 'done'
      return { ...cleared, state, result: answer.result }
    }
    if ('timedOut' in answer) return retryOrGiveUp(check, TIMEOUT_TEXT, now)
    const error = answer.error as Parameters<typeof toErrorOutcome>[0]
    const status = error?.response?.status
    const outcome = toErrorOutcome(error).error
    const notReady = status === 504 || (status === 404 && outcome.kind === 'failed')
    if (!notReady) return { ...cleared, state: 'error', error: outcome }
    return retryOrGiveUp(check, outcome.message ?? TIMEOUT_TEXT, now)
  })

// ENR-FR-005: retry in 15 s while the 5 minute window is open, then give up.
const retryOrGiveUp = (check: StorageCheck, lastMessage: string, now: Date): StorageCheck => {
  const elapsed = now.getTime() - new Date(check.windowStart as string).getTime()
  if (elapsed < RETRY_WINDOW_MS) {
    const notBefore = new Date(now.getTime() + RETRY_DELAY_MS).toISOString()
    return { ...check, state: 'queued', notBefore, lastMessage }
  }
  return { ...check, notBefore: undefined, state: 'gaveUp', lastMessage }
}

// ENR-FR-006: send an ended check again with a fresh 5 minute window.
export const retryCheck = (run: EnrollmentRun, serviceHost: string, now: Date): EnrollmentRun =>
  updateCheck(run, serviceHost, (check) => ({
    serviceHost: check.serviceHost,
    hostId: check.hostId,
    backend: check.backend,
    state: 'queued',
    windowStart: now.toISOString(),
  }))

// True while any host is still getting its roles.
const hasPendingHost = (run: EnrollmentRun) =>
  run.hosts.some((host) => host.phase === 'preparing' || host.phase === 'applying')

// ENR-FR-008: a run is active while roles are applying or checks are waiting or in flight.
export const isRunActive = (run: EnrollmentRun): boolean =>
  hasPendingHost(run) ||
  run.checks.some((check) => check.state === 'queued' || check.state === 'running')

// ENR-FR-008: remove the listed runs, but never one that is still active.
export const dismissRuns = (runs: EnrollmentRun[], ids: string[]): EnrollmentRun[] =>
  runs.filter((run) => !ids.includes(run.id) || isRunActive(run))

// ENR-FR-007: newest first, only the 10 newest are kept.
export const addRun = (runs: EnrollmentRun[], run: EnrollmentRun): EnrollmentRun[] =>
  [run, ...runs].slice(0, MAX_SAVED_RUNS)

// ENR-FR-002: resmgr polling is needed while any host is still getting its roles.
export const hostsPending = (runs: EnrollmentRun[]): boolean => runs.some(hasPendingHost)
